import { useState, useContext, useEffect } from "react";
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  InputAdornment,
  CircularProgress,
  Checkbox,
  Snackbar,
  Alert,
  Paper
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import API from "../../api/api";
import { AuthContext } from "../../context/AuthContext";
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
// import ChevronRightIcon from '@mui/icons-material/ChevronRight';
// import { fetchIP } from "../../utils/fetchIP";
import { registerNotificationToken } from "../../notifications/notification";

// 🖼️ Image Assets
import logoImage from "../../assets/mealno-brand-logo.svg";
import leaf1Img from "../../assets/leaf-1.png";
import leaf2Img from "../../assets/leaf-2.png";
import skylineImg from "../../assets/skyline.png";
import patternImg from "../../assets/pattern.png";

export default function RegisterPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  const [form, setForm] = useState({
    phone: "",
    name: "",
    email: "",
    referral_code: ""
  });

  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  
  const [phoneError, setPhoneError] = useState(false);

  // Check if the user came from Google Login
  const isGoogleVerified = !!location.state?.prefilled;

  useEffect(() => {
    if (isGoogleVerified) {
      const { name, email } = location.state.prefilled;
      setForm(prev => ({
        ...prev,
        name: name || "",
        email: email || ""
      }));
    }
  }, [location.state, isGoogleVerified]);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();

    if (!/^[a-zA-Z\s\-'\u00C0-\u017F]{2,50}$/.test(form.name.trim())) {
      setSnackbar({ open: true, message: "Please enter a valid name.", severity: "error" });
      return;
    }

    if (!/^[6-9]\d{9}$/.test(form.phone)) {
      setPhoneError(true); 
      setSnackbar({ open: true, message: "Please enter a valid 10-digit mobile number.", severity: "error" });
      return;
    }
    setPhoneError(false); 

    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setSnackbar({ open: true, message: "Please enter a valid email address.", severity: "error" });
      return;
    }

    setLoading(true);
    try {
      const res = await API.post("/register", {
        phone: form.phone,
        name: form.name,
        email: form.email,
        referral_code: form.referral_code,
        frontend_ip: null // Backend securely handles this now
      });

      if (res.data.error) {
        setSnackbar({ open: true, message: res.data.error, severity: "error" });
        return;
      }

      login(res.data);
      navigate("/home");

      registerNotificationToken(
        res.data.user.id,
        "USER",
        res.data.user.name,
        true
      ).catch(console.error);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.detail || "Registration failed. Try again.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const inputStyles = {
    mb: { xs: 1.5, sm: 2 },
    "& .MuiOutlinedInput-root": {
      borderRadius: "16px",
      bgcolor: "#FFFFFF",
      border: "1px solid #ECE5DA",
      transition: "all 0.2s ease-in-out",
      "& fieldset": { border: "none" },
      "&:hover": { borderColor: "#F8C332" },
      "&.Mui-focused": { border: "1px solid #F8C332", boxShadow: "0 0 0 3px rgba(248, 195, 50, 0.2)" },
      "&.Mui-disabled": { bgcolor: "#F5F3EF", border: "1px solid #EAE3D9" }
    },
    "& .MuiOutlinedInput-input": {
      fontWeight: 600,
      color: "#3A2C23",
      padding: { xs: "12px 14px", sm: "14px 14px" },
    },
    "& .Mui-disabled .MuiOutlinedInput-input": {
      color: "#A59D96",
      WebkitTextFillColor: "#A59D96"
    }
  };

  return (
    <Box sx={{ minHeight: "100dvh", display: "flex", flexDirection: "column", bgcolor: "#FFFDF8", position: "relative", overflowX: "hidden" }}>
      
      {/* Background Decor */}
      <Box component="img" src={leaf1Img} alt="Leaf" sx={{ position: 'absolute', top: 50, left: '5%', width: 20, zIndex: 0, pointerEvents: 'none', transform: 'rotate(-20deg)' }} />
      <Box component="img" src={leaf2Img} alt="Leaf" sx={{ position: 'absolute', top: 120, right: '8%', width: 24, zIndex: 0, pointerEvents: 'none', transform: 'rotate(15deg)' }} />
      <Box component="img" src={skylineImg} alt="Skyline" sx={{ position: 'absolute', top: '15%', left: 0, right: 0, width: '100%', opacity: 0.15, zIndex: 0, pointerEvents: 'none', objectFit: 'cover' }} />

      <Box sx={{ pt: "clamp(24px, 6vh, 64px)", pb: { xs: 1.5, sm: 3 }, textAlign: "center", zIndex: 2, px: 2, flexShrink: 0 }}>
        <img src={logoImage} alt="Mealno Logo" style={{ height: "42px", width: "auto", marginBottom: "8px" }} />
        <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: "#3A2C23", lineHeight: 1.3 }}>
          Complete Your <span style={{ color: "#6A9E3A" }}>Profile</span>
        </Typography>
      </Box>

      <Container maxWidth="xs" sx={{ zIndex: 3, position: "relative", flexShrink: 0 }}>
        <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: "24px", bgcolor: "#FFFFFF", boxShadow: "0 12px 32px rgba(58, 44, 35, 0.08)", border: "1px solid #ECE5DA", width: "100%", boxSizing: "border-box" }}>
          <Box component="form" onSubmit={handleRegisterSubmit} noValidate>
            
            <TextField
              fullWidth placeholder="Full Name" sx={inputStyles} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              InputProps={{ startAdornment: (<InputAdornment position="start"><PersonOutlineIcon sx={{ color: "#A59D96" }} /></InputAdornment>) }}
            />

            <TextField
              fullWidth
              placeholder="Email Address"
              sx={{ ...inputStyles, mb: isGoogleVerified ? 0.5 : { xs: 1.5, sm: 2 } }}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              disabled={isGoogleVerified} 
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineIcon sx={{ color: "#A59D96" }} />
                  </InputAdornment>
                ),
              }}
            />
            {isGoogleVerified && (
              <Typography variant="caption" sx={{ display: 'block', color: '#6A9E3A', fontWeight: 700, mb: 2, textAlign: 'left', pl: 1 }}>
                ✓ Email verified by Google
              </Typography>
            )}

            <TextField
              fullWidth type="tel" placeholder="Mobile Number *"
              sx={{
                ...inputStyles,
                "& .MuiOutlinedInput-root": {
                  ...inputStyles["& .MuiOutlinedInput-root"],
                  borderColor: phoneError ? "#E85D3F" : "#ECE5DA",
                  "&.Mui-focused": { border: `1px solid ${phoneError ? "#E85D3F" : "#F8C332"}` }
                }
              }}
              value={form.phone} error={phoneError} helperText={phoneError ? "Must be a valid 10-digit number" : ""}
              FormHelperTextProps={{ sx: { ml: 1, fontWeight: 600, color: "#E85D3F" } }}
              onChange={(e) => {
                setPhoneError(false);
                setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) });
              }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 10 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIphoneIcon fontSize="small" sx={{ color: phoneError ? "#E85D3F" : "#A59D96" }} />
                    <Typography sx={{ ml: 1, fontWeight: 700, color: phoneError ? "#E85D3F" : "#3A2C23" }}>+91</Typography>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth placeholder="Referral code (Optional)" sx={inputStyles} value={form.referral_code}
              onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
              InputProps={{ startAdornment: (<InputAdornment position="start"><Typography sx={{ fontWeight: 800, color: "#F8C332", fontSize: "0.85rem", ml: 0.5 }}>REF</Typography></InputAdornment>) }}
            />

            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3, textAlign: 'left', px: 0.5 }}>
              <Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} sx={{ p: 0, mr: 1.5, mt: 0.2, color: "#A59D96", '&.Mui-checked': { color: "#6A9E3A" } }} />
              <Typography variant="body2" sx={{ color: "#6E655E", lineHeight: 1.4, fontSize: "0.8rem", fontWeight: 500 }}>
                I agree to the <a href="https://terms.mealno.in/#/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#3A2C23", fontWeight: 700, textDecoration: 'underline' }}>Terms of Service</a> and <a href="https://terms.mealno.in/#/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#3A2C23", fontWeight: 700, textDecoration: 'underline' }}>Privacy Policy</a>
              </Typography>
            </Box>

            <Button
              type="submit" variant="contained" fullWidth size="large" disabled={loading || !agreed}
              sx={{
                py: 1.6, borderRadius: "16px", textTransform: "none", fontSize: "1.05rem", fontWeight: 800, color: "#FFFFFF",
                bgcolor: "#3A2C23", boxShadow: "0 8px 20px rgba(58, 44, 35, 0.2)",
                "&:hover": { bgcolor: "#2A1E17" }, "&.Mui-disabled": { bgcolor: "#EAE3D9", color: "#A59D96" }
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: "#FFF" }} /> : "Complete Registration"}
            </Button>
          </Box>
        </Paper>
      </Container>

      <Box sx={{ flexGrow: 1, minHeight: { xs: '15px', sm: '30px' } }} />

      <Box sx={{ width: "100%", height: "clamp(60px, 12vh, 120px)", bgcolor: "#F8D548", borderRadius: "100% 100% 0 0 / 40% 40% 0 0", zIndex: 1, display: "flex", justifyContent: "center", pt: 4, position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
        <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `url(${patternImg})`, backgroundSize: '150px', opacity: 0.12, zIndex: -1 }} />
        {/* <Typography onClick={() => navigate('/login')} sx={{ fontSize: "0.9rem", color: "#3A2C23", fontWeight: 600, display: "flex", alignItems: "center", cursor: "pointer", '&:hover': { opacity: 0.7 } }}>
          Already have an account? <span style={{ fontWeight: 800, marginLeft: "6px" }}>Login</span> 
          <ChevronRightIcon fontSize="small" sx={{ ml: 0.5 }} />
        </Typography> */}
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: '16px', fontWeight: 700, bgcolor: snackbar.severity === 'error' ? '#E85D3F' : '#6A9E3A' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}